/**
 * Outbound messaging from the dashboard to the RISA browser extension.
 *
 * The extension content script listens for `window` `message` events; the
 * dashboard posts back using a dedicated source identifier so the extension can
 * distinguish dashboard-originated messages from its own.
 */

/** Source identifier stamped on messages the dashboard posts to the extension. */
export const RISA_DASHBOARD_SOURCE = "RISA_DASHBOARD";

/** Message type asking the extension to tear down its paired session. */
export const EXTENSION_LOG_OUT_TYPE = "LOG_OUT";

/** Message type asking the extension to report its current auth state. */
export const EXTENSION_GET_AUTH_STATE_TYPE = "GET_AUTH_STATE";

/** Message type handing a freshly generated pairing code to the extension. */
export const EXTENSION_PAIRING_CODE_TYPE = "PAIRING_CODE";

/**
 * Ask the paired RISA extension to log out.
 *
 * Posts a `LOG_OUT` message on the current window. Used whenever the dashboard's
 * RBAC session is invalidated (token rotation / facility switch / logout) so the
 * extension's cached session is never left stale.
 *
 * @returns void
 */
export function notifyExtensionLogOut(): void {
  if (typeof window === "undefined") return;

  window.postMessage(
    { source: RISA_DASHBOARD_SOURCE, type: EXTENSION_LOG_OUT_TYPE },
    window.location.origin,
  );

  console.info("[RISA_DASHBOARD] emitted LOG_OUT event");
}

/**
 * Ask the paired RISA extension to report its current auth state.
 *
 * Posts a `GET_AUTH_STATE` message; the extension is expected to reply with an
 * `AUTH_STATE` message carrying `{ deviceId, loggedIn }`.
 *
 * @returns void
 */
export function requestExtensionAuthState(): void {
  if (typeof window === "undefined") return;

  window.postMessage(
    { source: RISA_DASHBOARD_SOURCE, type: EXTENSION_GET_AUTH_STATE_TYPE },
    window.location.origin,
  );

  console.info("[RISA_DASHBOARD] requested GET_AUTH_STATE");
}

/**
 * Hand a freshly generated pairing code to the paired RISA extension.
 *
 * Posts a `PAIRING_CODE` message carrying `{ code, deviceId }` so the extension
 * can auto-redeem it (via the public `/auth/extension/redeem` endpoint) and open
 * its logged-in state without the user copy-pasting the code manually. The
 * extension is still expected to reply with a `LOGGED_IN` message once redemption
 * succeeds so the dashboard can flip to the connected state.
 *
 * @param code - The pairing code returned by the pairing-code endpoint
 * @param deviceId - The device id the code was generated for (must match on redeem)
 * @returns void
 */
export function sendPairingCodeToExtension(
  code: string,
  deviceId: string,
): void {
  if (typeof window === "undefined") return;

  window.postMessage(
    {
      source: RISA_DASHBOARD_SOURCE,
      type: EXTENSION_PAIRING_CODE_TYPE,
      payload: { code, deviceId },
    },
    window.location.origin,
  );

  console.info("[RISA_DASHBOARD] sent PAIRING_CODE to extension");
}
